import { useCallback, useEffect, useRef, useState } from 'react';

import type { Chain } from './api';
import { apply, attach, hear, wake, type Graph } from './preview';

/**
 * The Original / Mastered toggle, and the waveform under it.
 *
 * ## Two players, one clock, always both running
 *
 * Both files play at once, from the same moment, and the toggle only
 * decides which one is audible. That is the whole trick: switching
 * `muted` on two elements that are already in step is instantaneous and
 * gapless, where pausing one and seeking the other to its time never is.
 * This is the interaction people judge the product by. If the switch
 * clicks or jumps, nothing else on the page matters.
 *
 * Kept in step: the mastered side is seeked to the original's time on
 * play, on seek, and whenever the two drift past a tenth of a second,
 * which a slow network can cause once the buffers differ.
 *
 * ## Preview, the third side
 *
 * The original element is tapped into a WebAudio graph that approximates
 * the chain (`preview.ts`), live. "Preview" is that graph's wet path;
 * "Original" is its dry path. Both come from the same element, so the
 * three sides share one clock whatever the mastered file is doing.
 *
 * ## The waveform is the source's
 *
 * Two thousand peaks from the render's analysis, drawn on a canvas. It
 * does not change between Original and Mastered, on purpose: it is the
 * ruler, not the picture of what changed. The numbers under it say that.
 */
export function AbPlayer({
  originalUrl,
  masteredUrl,
  peaks,
  duration,
  side,
  onSide,
  preview,
}: {
  originalUrl: string | null;
  masteredUrl: string | null;
  peaks: number[] | null;
  duration: number;
  side: Side;
  onSide: (side: Side) => void;
  /** The composed chain to approximate, and the track's measured loudness. Null: no preview yet. */
  preview: { chain: Chain; input_lufs: number | null } | null;
}) {
  const a = useRef<HTMLAudioElement>(null);
  const b = useRef<HTMLAudioElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [loop, setLoop] = useState(false);
  const [ready, setReady] = useState({ a: false, b: false });

  const graph = useRef<Graph | null>(null);
  const canB = masteredUrl !== null;
  const canPreview = preview !== null;
  const hearB = side === 'mastered' && canB;
  const hearPreview = side === 'preview' && canPreview;

  // Which is audible. Changing this is the whole toggle: the mastered
  // element's mute, and which of the graph's two paths is up.
  useEffect(() => {
    if (a.current) a.current.muted = hearB;
    if (b.current) b.current.muted = !hearB;
    if (graph.current) hear(graph.current, hearPreview ? 'preview' : 'original');
  }, [hearB, hearPreview]);

  // The chain, onto the graph, whenever it changes.
  useEffect(() => {
    if (graph.current && preview) apply(graph.current, preview.chain, preview.input_lufs);
  }, [preview]);

  // A new track, or a new render: back to the start, in step.
  useEffect(() => {
    setPlaying(false);
    setPosition(0);
    setReady({ a: false, b: false });
  }, [originalUrl, masteredUrl]);

  const align = useCallback(() => {
    if (!a.current || !b.current || !canB) return;

    if (Math.abs(a.current.currentTime - b.current.currentTime) > 0.1) {
      b.current.currentTime = a.current.currentTime;
    }
  }, [canB]);

  const play = useCallback(async () => {
    if (!a.current) return;

    align();

    /*
     * The tap, made on the first play (a gesture is what lets a context
     * run) and remade whenever the element is a different one: React
     * remounts it when a track changes, and a graph bound to the old
     * element is a graph feeding silence from something no longer on the
     * page while the new element plays untouched, which reads as "the
     * preview stopped working".
     */
    if (!graph.current || graph.current.source.mediaElement !== a.current) {
      try {
        graph.current = attach(a.current);

        if (preview) apply(graph.current, preview.chain, preview.input_lufs);

        hear(graph.current, hearPreview ? 'preview' : 'original');
      } catch {
        // No WebAudio here. Original and Mastered still work; Preview will not.
      }
    }

    if (graph.current) await wake(graph.current);

    try {
      await Promise.all([a.current.play(), canB && b.current ? b.current.play() : Promise.resolve()]);
      setPlaying(true);
    } catch {
      // Autoplay refused, or a link that has died. The button stays a play button.
    }
  }, [align, canB, preview, hearPreview]);

  const pause = useCallback(() => {
    a.current?.pause();
    b.current?.pause();
    setPlaying(false);
  }, []);

  const seek = useCallback(
    (seconds: number) => {
      const at = Math.max(0, Math.min(duration || 0, seconds));

      if (a.current) a.current.currentTime = at;
      if (b.current) b.current.currentTime = at;

      setPosition(at);
    },
    [duration],
  );

  // The clock, and the drift check, from the original.
  useEffect(() => {
    const el = a.current;

    if (!el) return;

    const tick = () => {
      setPosition(el.currentTime);
      align();
    };
    const ended = () => {
      if (loop) {
        seek(0);
        void play();
      } else {
        setPlaying(false);
      }
    };

    el.addEventListener('timeupdate', tick);
    el.addEventListener('ended', ended);

    return () => {
      el.removeEventListener('timeupdate', tick);
      el.removeEventListener('ended', ended);
    };
  }, [align, loop, play, seek]);

  // The waveform, drawn once per peaks array and once per position change
  // (cheap: two thousand rectangles).
  useEffect(() => {
    const el = canvas.current;

    if (!el) return;

    const dpr = window.devicePixelRatio || 1;
    const width = el.clientWidth;
    const height = el.clientHeight;

    el.width = Math.floor(width * dpr);
    el.height = Math.floor(height * dpr);

    const ctx = el.getContext('2d');

    if (!ctx) return;

    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);

    const data = peaks && peaks.length > 0 ? peaks : Array.from({ length: 400 }, () => 0.05);
    const played = duration > 0 ? position / duration : 0;
    const styles = getComputedStyle(el);
    const lit = styles.getPropertyValue('--color-accent').trim() || '#7b84f2';
    const dim = styles.getPropertyValue('--color-line-strong').trim() || '#3a3a5a';
    const mid = height / 2;
    const step = width / data.length;

    for (let i = 0; i < data.length; i++) {
      const value = Math.max(0.02, Math.min(1, data[i] ?? 0));
      const h = value * (height - 4);
      const x = i * step;

      ctx.fillStyle = x / width <= played ? lit : dim;
      ctx.fillRect(x, mid - h / 2, Math.max(1, step - 0.5), h);
    }
  }, [peaks, position, duration]);

  const onCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const fraction = (event.clientX - rect.left) / rect.width;

    seek(fraction * duration);
  };

  const both = ready.a && (!canB || ready.b);

  return (
    <div className="flex flex-col gap-3" data-ab-player data-side={hearB ? 'mastered' : hearPreview ? 'preview' : 'original'}>
      {/*
        Always mounted, with the source swapped, so the WebAudio tap on it
        survives a change of track. `crossOrigin`, or the tap hears silence
        from a tainted element.
      */}
      <audio
        ref={a}
        src={originalUrl ?? undefined}
        crossOrigin="anonymous"
        preload="auto"
        onCanPlay={() => setReady((r) => ({ ...r, a: true }))}
      />
      {masteredUrl && (
        <audio ref={b} src={masteredUrl} preload="auto" onCanPlay={() => setReady((r) => ({ ...r, b: true }))} />
      )}

      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => (playing ? pause() : void play())}
          disabled={!originalUrl || !both}
          aria-label={playing ? 'Pause' : 'Play'}
          data-ab-play
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-ink text-app transition-opacity disabled:opacity-40"
        >
          {playing ? (
            <svg viewBox="0 0 16 16" className="h-5 w-5" fill="currentColor" aria-hidden="true">
              <rect x="3" y="2" width="4" height="12" rx="1" />
              <rect x="9" y="2" width="4" height="12" rx="1" />
            </svg>
          ) : (
            <svg viewBox="0 0 16 16" className="h-5 w-5 translate-x-0.5" fill="currentColor" aria-hidden="true">
              <path d="M4.5 2.8v10.4a.6.6 0 0 0 .93.5l8-5.2a.6.6 0 0 0 0-1l-8-5.2a.6.6 0 0 0-.93.5Z" />
            </svg>
          )}
        </button>

        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <canvas
            ref={canvas}
            onClick={onCanvasClick}
            className="h-28 w-full cursor-pointer rounded-[var(--radius-control)] lg:h-36"
            aria-label="Waveform; click to seek"
            data-ab-waveform
          />
          <div className="flex justify-between font-mono text-[11px] text-faint">
            <span>{clock(position)}</span>
            <span>{clock(duration)}</span>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setLoop((on) => !on)}
          aria-pressed={loop}
          className={`shrink-0 rounded-[var(--radius-pill)] border px-3 py-1.5 text-xs transition-colors ${
            loop ? 'border-accent text-accent' : 'border-line text-soft hover:text-ink'
          }`}
          data-ab-loop
        >
          Loop
        </button>
      </div>

      <div className="flex items-center gap-3">
        <div className="inline-flex rounded-[var(--radius-pill)] border border-line p-0.5" role="group" aria-label="Listen to">
          <SideButton on={!hearB && !hearPreview} onClick={() => onSide('original')} label="Original" />
          <SideButton on={hearPreview} onClick={() => onSide('preview')} label="Preview" disabled={!canPreview} />
          <SideButton on={hearB} onClick={() => onSide('mastered')} label="Mastered" disabled={!canB} />
        </div>
        <span className="text-xs text-faint">
          {hearPreview
            ? 'An approximation, live in your browser. The master is the real thing.'
            : !canB
              ? 'Preview follows the tile and the knobs; Master it for the real file.'
              : ''}
        </span>
      </div>
    </div>
  );
}

export type Side = 'original' | 'preview' | 'mastered';

function SideButton({ on, onClick, label, disabled = false }: { on: boolean; onClick: () => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={on}
      data-ab-side={label.toLowerCase()}
      className={`rounded-[var(--radius-pill)] px-4 py-1.5 text-sm font-medium transition-colors disabled:opacity-40 ${
        on ? 'bg-raised text-ink' : 'text-soft hover:text-ink'
      }`}
    >
      {label}
    </button>
  );
}

function clock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';

  const total = Math.floor(seconds);

  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
